import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { EditorView } from 'prosemirror-view';
import { useIntl } from 'react-intl';
import {
	Dropdown,
	DropdownMenu,
	DropdownToggle
} from 'reactstrap';

import { messages } from '../../../../../../_shared/i18n/messages';

import { ToolbarButton } from '../../../Toolbar';
import { insertMergeTag } from '../commands';

import MergeTagSelect from './MergeTagSelect';

import styles from './MergeTagButton.module.scss';

const propTypes = {

	/**
	 * Set of merge tags that are available for use.
	 */
	mergeTags: PropTypes.shape({

		organization: PropTypes.shape({

			domain: PropTypes.bool,

		}),

		invitation: PropTypes.shape({

			id: PropTypes.bool,

		}),

	}),

	/**
	 * The id of the DOM element where the editor will be injected.
	 */
	editorDomId: PropTypes.string.isRequired,

	/*
	 * The prosemirror editor view object.
	 */
	editorView: PropTypes.instanceOf(EditorView).isRequired,

	/**
	 * The provided toolbar props to adhere to accessibility (a11y)
	 * guidelines for button toolbars.
	 *
	 * See here: https://www.w3.org/WAI/ARIA/apg/patterns/toolbar
	 */
	a11yToolbarProps: PropTypes.shape({

		/**
		 * The tab index value for the button. Only the button with
		 * current focus should have tab index value of 0. All others
		 * should have value of -1.
		 */
		tabIndex: PropTypes.oneOf(['0', '-1']).isRequired,

		/**
		 * Callback function to handle keypress events on the button.
		 */
		onKeyDown: PropTypes.func.isRequired,

	}),

};

const MergeTagButton = (props) => {

	const {
		mergeTags,
		editorDomId,
		editorView,
		a11yToolbarProps = {},
	} = props;

	const intl = useIntl();

	const [showDropdown, setShowDropdown] = useState(false);

	const handleOnSelect = (option, { action }) => {

		const { selection: { from, to } } = editorView.state;

		if (action === 'select-option') {
			insertMergeTag(option.value, from, to)(editorView.state, editorView.dispatch);
			editorView.focus();
			setShowDropdown(false);
		}

	};

	return (
		<Dropdown
			className={styles['merge-tag-button-dropdown']}
			isOpen={showDropdown}
			toggle={() => setShowDropdown((prev) => !prev)}>
			<DropdownToggle
				tag={ToolbarButton}
				id={`${editorDomId}-toolbar-btn-merge-tag`}
				icon="far fa-tag"
				disabled={false}
				title={intl.formatMessage(messages.TC_COMPONENTS_TEXT_EDITOR_MERGE_TAG_BUTTON_TITLE)}
				{...a11yToolbarProps} />
			<DropdownMenu>
				<MergeTagSelect
					mergeTags={mergeTags}
					onChange={handleOnSelect} />
			</DropdownMenu>
		</Dropdown>
	);

};

MergeTagButton.propTypes = propTypes;

export default MergeTagButton;
